import {
  Avatar,
  Box,
  BoxList,
  Button,
  Icon,
  Spinner,
  Text,
} from '@factorialco/deprecated-design-system'
import { SignerStampsDocument } from 'generated/resources/graphql'
import { DropResult } from 'react-beautiful-dnd'

import { createPortal } from 'react-dom'
import { Draggable } from 'react-beautiful-dnd'

import { DragAndDropContext, DropableBox } from 'modules/core/components/DragAndDrop'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

interface Props {
  additionalSigners: string[]
  removeRecipient: () => void
  removeSigner: (accessId: string) => void
  showRecipient?: boolean
  sequentialSigning?: boolean
  onReorder?: (reorderedAccessIds: string[]) => void
}

const SignersList = ({
  additionalSigners,
  removeRecipient,
  removeSigner,
  showRecipient = true,
  sequentialSigning = false,
  onReorder,
}: Props) => {
  const { data, loading } = useQuery(SignerStampsDocument, {
    variables: { signerIds: additionalSigners },
    skip: additionalSigners.length === 0,
  })

  const rawSigners = data?.apiCore.accessesConnection.nodes ?? []

  // Sort signers to match the order of additionalSigners (input array order).
  // UI displays 1-based indices (#1, #2, ...) while backend signing_order is 0-based.
  const signerOrderMap = new Map(additionalSigners.map((id, idx) => [id, idx]))
  const signers = [...rawSigners].sort((a, b) => {
    const indexA = signerOrderMap.get(a.id) ?? additionalSigners.length
    const indexB = signerOrderMap.get(b.id) ?? additionalSigners.length
    return indexA - indexB
  })

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !onReorder) return
    if (result.source.index === result.destination.index) return

    const reordered = [...additionalSigners]
    const moved = reordered.splice(result.source.index, 1)[0]
    if (!moved) return
    reordered.splice(result.destination.index, 0, moved)
    onReorder(reordered)
  }

  if (loading) {
    return (
      <Box alignItems="center" justifyContent="center" padding="s12">
        <Spinner />
      </Box>
    )
  }

  const renderSignerRow = (signer: (typeof signers)[number], index: number) => (
    <Box
      key={signer.id}
      flexDirection="row"
      padding="s12"
      gap="s8"
      alignItems="center"
      justifyContent="spaceBetween"
      border={
        index !== 0
          ? {
              top: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }
          : {}
      }
    >
      <Box gap="s8" flexDirection="row" alignItems="center">
        {sequentialSigning && <Icon.Medium icon="drag-and-drop" color="grey800" />}
        <Avatar label={signer.fullName} url={signer.avatar || ''} size="s40" />
        <Box>
          <Text.Normal weight="semibold">{signer.fullName}</Text.Normal>
          <Text.Normal size="100">{signer.employee?.referenceContract?.jobTitle ?? ''}</Text.Normal>
        </Box>
      </Box>
      <Button type="secondary" iconName="cross" onClick={() => removeSigner(signer.id)} />
    </Box>
  )

  const recipientRow = showRecipient && (
    <Box
      flexDirection="row"
      padding="s12"
      gap="s8"
      justifyContent="spaceBetween"
      alignItems="center"
    >
      <Box gap="s8" flexDirection="row" alignItems="center">
        <Box padding="s4" background="grey200" borderRadius={{ all: 'abs012' }}>
          <Icon.Large icon="user" />
        </Box>
        <Text.Normal weight="semibold">{i18n.t('split_documents.form.recipient')}</Text.Normal>
      </Box>
      <Button type="secondary" iconName="cross" onClick={removeRecipient} />
    </Box>
  )

  if (sequentialSigning) {
    return (
      <DragAndDropContext onDragEnd={handleDragEnd}>
        {recipientRow}
        <Box flexDirection="row" gap="s8" alignItems="stretch">
          <Box flexDirection="column" justifyContent="spaceAround">
            {signers.map((_, index) => (
              <Box key={index} alignItems="center" justifyContent="center" flexGrow>
                <Text.Normal size="200" weight="semibold" color="grey800">
                  #{index + 1}
                </Text.Normal>
              </Box>
            ))}
          </Box>
          <Box flexGrow>
            <BoxList>
              <DropableBox droppableId="signers-list" type="signers">
                {signers.map((signer, index) => (
                  <Draggable key={signer.id} draggableId={`step-${signer.id}`} index={index}>
                    {(provided, snapshot) => {
                      const child = (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          {renderSignerRow(signer, index)}
                        </div>
                      )
                      // Portal to body when dragging to escape modal's transform context
                      return snapshot.isDragging ? createPortal(child, document.body) : child
                    }}
                  </Draggable>
                ))}
              </DropableBox>
            </BoxList>
          </Box>
        </Box>
      </DragAndDropContext>
    )
  }

  return (
    <BoxList>
      {recipientRow}
      {signers.map((signer, index) => renderSignerRow(signer, index))}
    </BoxList>
  )
}

export default SignersList
